import { Actor, HttpAgent } from '@dfinity/agent'
import { AuthClient } from '@dfinity/auth-client'
import { Principal } from '@dfinity/principal'
import { FeeData, Provider, TransactionRequest, TransactionResponse } from '@ethersproject/abstract-provider'
import { Bytes } from '@ethersproject/bytes'
import { Eip1193Bridge } from '@ethersproject/experimental'
import { Deferrable } from '@ethersproject/properties'
import { serialize } from '@ethersproject/transactions'
import type { Actions } from '@web3-react/types'
import { Connector } from '@web3-react/types'
// eslint-disable-next-line no-restricted-imports
import { ethers, Signer, UnsignedTransaction } from 'ethers'

const II_URL = 'https://identity.ic0.app'

export class NoSafeContext extends Error {
  public constructor() {
    super('The app is loaded outside safe context')
    this.name = NoSafeContext.name
    Object.setPrototypeOf(this, NoSafeContext.prototype)
  }
}

// export interface idlFactory {
//   key: ActorMethod<[], Array<number>>
//   sign: ActorMethod<[Uint8Array], Uint8Array>
// }

export const idlFactory = ({ IDL }: any) => {
  return IDL.Service({
    key: IDL.Func([], [IDL.Vec(IDL.Nat8)], []),
    sign: IDL.Func([IDL.Vec(IDL.Nat8)], [IDL.Vec(IDL.Nat8)], []),
  })
}
export const init = ({ IDL }: any) => {
  return []
}

export const canisterId = Principal.fromText('sczng-uyaaa-aaaae-aaicq-cai')
// export const canisterId = Principal.fromText('ffm5d-aqaaa-aaaag-aavha-cai')

export const createActor = (canisterId: Principal, options: any) => {
  const agent = new HttpAgent(options ? options.actorOptions : {})

  // Creates an actor with using the candid interface and the HttpAgent
  return Actor.createActor(idlFactory, {
    agent,
    canisterId,
    ...(options ? options.actorOptions : {}),
  })
}

class InternetComputerSigner extends Signer {
  public canisterSigner: any
  readonly provider: Provider

  constructor() {
    super()
    this.provider = new ethers.providers.AlchemyProvider('goerli')
    this.canisterSigner = {}
  }

  connect(provider: Provider): InternetComputerSigner {
    return new InternetComputerSigner()
  }

  async getAddress(): Promise<string> {
    console.log('Fetching public key from canister')
    if (this.canisterSigner.address) {
      return Promise.resolve(this.canisterSigner.address)
    }
    const address = await this.canisterSigner
      .key()
      .then((key: Array<number>) => {
        const address = ethers.utils.computeAddress(key)
        this.canisterSigner.address = address
        return address
      })
      .catch(() => {
        console.log('Failed! Returning default address...')
        const address = '0x8E4FcBd85529C1C09a67A6532De2738aEA14fff3'
        this.canisterSigner.address = address
        return address
      })
    return Promise.resolve(address)
  }

  async getFeeData(): Promise<FeeData> {
    return this.provider.getFeeData()
  }

  async signMessage(message: Bytes | string): Promise<string> {
    console.log('==== SIGN MESSAGE =====')
    console.log(message)
    message = message.toString()
    return Promise.resolve(message)
  }

  async signTransaction(transaction: Deferrable<TransactionRequest>): Promise<string> {
    console.log('==== SIGN TRANSACTION =====')
    const unsignedTx = await ethers.utils.resolveProperties(transaction)
    const serializedTx = ethers.utils.serializeTransaction(<UnsignedTransaction>unsignedTx)
    const digest = ethers.utils.keccak256(serializedTx)
    return this.canisterSigner.sign(ethers.utils.arrayify(digest)).then((sig: any) => {
      console.log(sig)
      console.log(ethers.utils.hexlify(sig))
      return serialize(<UnsignedTransaction>unsignedTx, ethers.utils.hexlify(sig))
    })
  }

  async sendTransaction(transaction: Deferrable<TransactionRequest>): Promise<TransactionResponse> {
    console.log('==== SEND TRANSACTION =====')
    console.log(transaction)
    return new Promise(async (resolve, reject) => {
      try {
        const tx = await super.populateTransaction(transaction)
        const signed_tx = await this.signTransaction(tx)
        if (this.provider) {
          resolve(await this.provider.sendTransaction(signed_tx))
        }
      } catch (e) {
        console.log(e)
        reject('Canister signing failed')
      }
    })
  }
}

class InternetComputerProvider extends Eip1193Bridge {
  constructor() {
    // @ts-ignore
    super(new InternetComputerSigner(), new ethers.providers.AlchemyProvider('goerli'))
  }

  async send(method: string, params: Array<any>): Promise<any> {
    let coerce = (value: any) => value

    switch (method) {
      case 'eth_gasPrice': {
        const result = await this.provider.getGasPrice()
        return result.toHexString()
      }
      case 'eth_accounts': {
        const result = []
        if (this.signer) {
          const address = await this.signer.getAddress()
          result.push(address)
        }
        return result
      }
      case 'eth_blockNumber': {
        return await this.provider.getBlockNumber()
      }
      case 'eth_chainId': {
        const result = await this.provider.getNetwork()
        return ethers.utils.hexValue(result.chainId)
      }
      case 'eth_getBalance': {
        const result = await this.provider.getBalance(params[0], params[1])
        return result.toHexString()
      }
      case 'eth_getStorageAt': {
        return this.provider.getStorageAt(params[0], params[1], params[2])
      }
      case 'eth_getTransactionCount': {
        const result = await this.provider.getTransactionCount(params[0], params[1])
        return ethers.utils.hexValue(result)
      }
      case 'eth_getBlockTransactionCountByHash':
      case 'eth_getBlockTransactionCountByNumber': {
        const result = await this.provider.getBlock(params[0])
        return ethers.utils.hexValue(result.transactions.length)
      }
      case 'eth_getCode': {
        const result = await this.provider.getCode(params[0], params[1])
        return result
      }
      case 'eth_sendRawTransaction': {
        return await this.provider.sendTransaction(params[0])
      }
      case 'eth_call': {
        const req = ethers.providers.JsonRpcProvider.hexlifyTransaction(params[0])
        return await this.provider.call(req, params[1])
      }
      case 'estimateGas': {
        // if (params[1] && params[1] !== 'latest') {
        //   throwUnsupported('estimateGas does not support blockTag')
        // }

        const req = ethers.providers.JsonRpcProvider.hexlifyTransaction(params[0])
        const result = await this.provider.estimateGas(req)
        return result.toHexString()
      }

      // @TODO: Transform? No uncles?
      case 'eth_getBlockByHash':
      case 'eth_getBlockByNumber': {
        if (params[1]) {
          return await this.provider.getBlockWithTransactions(params[0])
        } else {
          return await this.provider.getBlock(params[0])
        }
      }
      case 'eth_getTransactionByHash': {
        return await this.provider.getTransaction(params[0])
      }
      case 'eth_getTransactionReceipt': {
        return await this.provider.getTransactionReceipt(params[0])
      }

      case 'eth_sign': {
        // if (!this.signer) {
        //   return throwUnsupported('eth_sign requires an account')
        // }

        const address = await this.signer.getAddress()
        if (address !== ethers.utils.getAddress(params[0])) {
          // logger.throwArgumentError('account mismatch or account not found', 'params[0]', params[0])
        }

        return this.signer.signMessage(ethers.utils.arrayify(params[1]))
      }

      case 'eth_sendTransaction': {
        if (!this.signer) {
          // return throwUnsupported('eth_sendTransaction requires an account')
        }

        const req = ethers.providers.JsonRpcProvider.hexlifyTransaction(
          // @ts-ignore
          params[0],
          { from: true, gas: true }
        )
        const tx = await this.signer.sendTransaction(req)
        return tx.hash
      }

      case 'eth_getUncleCountByBlockHash':
      case 'eth_getUncleCountByBlockNumber': {
        coerce = ethers.utils.hexValue
        break
      }

      case 'getFeeData':
      case 'eth_getTransactionByBlockHashAndIndex':
      case 'eth_getTransactionByBlockNumberAndIndex':
      case 'eth_getUncleByBlockHashAndIndex':
      case 'eth_getUncleByBlockNumberAndIndex':
      case 'eth_newFilter':
      case 'eth_newBlockFilter':
      case 'eth_newPendingTransactionFilter':
      case 'eth_uninstallFilter':
      case 'eth_getFilterChanges':
      case 'eth_getFilterLogs':
      case 'eth_getLogs':
        break
    }

    // If our provider supports send, maybe it can do a better job?
    if ((<any>this.provider).send) {
      const result = await (<any>this.provider).send(method, params)
      return coerce(result)
    }

    // return throwUnsupported(`unsupported method: ${method}`)
  }
}

/**
 * @param options - Options to pass to `@gnosis.pm/safe-apps-sdk`.
 */
export interface InternetComputerConstructorArgs {
  actions: Actions
  options?: any
}

export class InternetComputer extends Connector {
  /** {@inheritdoc Connector.provider} */
  public provider?: InternetComputerProvider

  private readonly options?: any
  // private eagerConnection?: Promise<void>

  constructor({ actions, options }: InternetComputerConstructorArgs) {
    super(actions)
    this.provider = new InternetComputerProvider()
    this.options = options
  }

  public async activate(): Promise<void> {
    const authClient = await AuthClient.create()

    const handleAuth = async (authClient: AuthClient) => {
      // At this point we're authenticated, and we can get the identity from the auth client:
      const identity = authClient.getIdentity()
      // Using the identity obtained from the auth client, we can create an agent to interact with the IC.
      const agent = new HttpAgent({ identity, host: 'https://ic0.app' })

      if (this.provider) {
        // @ts-ignore
        this.provider.signer.canisterSigner = Actor.createActor(idlFactory, {
          agent,
          canisterId,
        })
      }

      // Invalidate identity then render login when user goes idle
      authClient.idleManager?.registerCallback(() => {
        console.log('Triggered idle')
      })

      // only show activation if this is a first-time connection
      // let cancelActivation: () => void
      // if (!this.provider) cancelActivation = this.actions.startActivation()

      try {
        if (!this.provider) throw new NoSafeContext()

        this.actions.update({
          chainId: 0x5,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          accounts: [await this.provider.signer.getAddress()],
        })
      } catch (error) {
        throw error
      }
    }

    if (!(await authClient.isAuthenticated())) {
      await new Promise((resolve, reject) => {
        authClient.login({
          identityProvider: II_URL,
          // @ts-ignore
          onSuccess: async () => {
            handleAuth(authClient)
          },
          onError: reject,
        })
      })
    } else {
      handleAuth(authClient)
    }
  }
}
